/**
 * Engineering Thresholds for Context-Aware Scoring
 * 
 * These thresholds are based on real-world physics and engineering practice:
 * - They determine when certain configurations become advantageous or disadvantageous
 * - They enable conditional scoring that adapts to system load and architecture
 * - They prevent artificial bias by making scoring contextual
 */

/**
 * Heat Load Threshold for Zoning Advantage (kW)
 * 
 * Above 18 kW heat loss:
 * - S-Plan gains advantage due to better flow separation
 * - Independent zone control becomes more critical
 * - Control stability improves with dedicated circuits
 * 
 * At or below 18 kW:
 * - Y-Plan and S-Plan are both acceptable
 * - Flow management is less critical
 * - No artificial bias applied
 * 
 * Rationale: Larger loads (>18 kW) benefit from proper hydraulic separation.
 * Smaller loads can work effectively with either topology.
 */
export const LOAD_ZONING_THRESHOLD_KW = 18;

/**
 * Heat Load Threshold for Primary Pipe Sizing (kW)
 * 
 * At or above 19 kW heat loss:
 * - 28mm primaries become advantageous
 * - Higher flow rates require larger pipes to avoid velocity noise
 * - Lower pressure drop across circuit improves performance
 * 
 * Below 19 kW:
 * - 28mm primaries are oversized (mild disadvantage)
 * - Higher water volume reduces response time
 * - More standing heat mass in pipework
 * - Increased material cost without benefit
 * 
 * Rationale: Pipe sizing should match system load. Oversizing below 19 kW
 * creates slower response and unnecessary thermal mass. Undersizing above 19 kW
 * creates velocity noise and flow restriction.
 */
export const LOAD_PRIMARY_THRESHOLD_KW = 19;

/**
 * Threshold Labels for UI Explanations
 * These provide context-aware explanations for why scoring changes at thresholds
 */
export const THRESHOLD_EXPLANATIONS = {
  ZONING_ABOVE_THRESHOLD: (loadKw: number) => 
    `Heat demand above ${LOAD_ZONING_THRESHOLD_KW} kW benefits from zoning stability – S-Plan provides better flow separation`,
  
  ZONING_BELOW_THRESHOLD: (loadKw: number) => 
    `Heat demand at ${loadKw} kW – both Y-Plan and S-Plan work well at this load`,
  
  PRIMARY_28MM_ADVANTAGE: (loadKw: number) => 
    `Heat demand ${loadKw} kW – 28mm primaries provide better flow with lower velocity noise`,
  
  PRIMARY_28MM_OVERSIZED: (loadKw: number) => 
    `Property load below ${LOAD_PRIMARY_THRESHOLD_KW} kW – oversized primaries reduce responsiveness and increase standing mass`,
  
  ARCHITECTURE_UNVENTED_UPGRADE: () => 
    `Unvented cylinder architecture removes gravity constraints and improves DHW delivery quality`,
  
  CYLINDER_MIXERGY_BENEFIT: () => 
    `Mixergy cylinder provides improved standing loss performance, PV synergy, and electrification readiness`,
} as const;

/**
 * Score adjustments for conditional logic
 */
export const CONDITIONAL_SCORE_ADJUSTMENTS = {
  /** S-Plan bonus when heat load exceeds zoning threshold */
  ZONING_BONUS_ABOVE_THRESHOLD: 10,
  
  /** 28mm primary advantage when load exceeds primary threshold */
  PRIMARY_28MM_BONUS_ABOVE_THRESHOLD: 8,
  
  /** 28mm primary disadvantage when load is below primary threshold */
  PRIMARY_28MM_PENALTY_BELOW_THRESHOLD: -6,
  
  /** Unvented cylinder DHW delivery baseline improvement */
  UNVENTED_DHW_BASELINE_BOOST: 10,
  
  /** Mixergy bonus for unvented DHW delivery */
  MIXERGY_UNVENTED_DHW_BONUS: 5,
} as const;
